import { useEffect, useRef } from 'react';

const VERSION_RELOAD_KEY = 'versionReloadAttempted';

/**
 * Custom hook to check for application version updates and force reload if needed.
 * Reloads at most once per browser session to avoid infinite loops on version mismatch.
 */
const useVersionChecker = (): void => {
    const currentVersion = useRef(process.env.REACT_APP_VERSION ?? '0.0.0');

    useEffect(() => {
        const checkVersion = async (): Promise<void> => {
            // Prevent reload loop: only attempt once per browser session
            if (sessionStorage.getItem(VERSION_RELOAD_KEY)) {
                console.log('[useVersionChecker] Reload already attempted this session, skipping.');
                return;
            }

            try {
                const response = await fetch(
                    `${process.env.PUBLIC_URL}/api/AppSettings/GetLastUpdates?version=${currentVersion.current}&t=${Date.now()}`,
                    {
                        method: 'GET',
                        cache: 'no-cache',
                        headers: {
                            'Cache-Control': 'no-cache',
                            'Pragma': 'no-cache'
                        }
                    }
                );

                if (response.ok) {
                    const etlUpdates = await response.json();
                    const appVersionEntry = etlUpdates.find((update: { label?: string }) =>
                        update.label?.startsWith('Application ')
                    );

                    if (appVersionEntry) {
                        const serverVersion = (appVersionEntry.label as string).replace('Application ', '').trim();

                        if (serverVersion !== currentVersion.current) {
                            console.log(`[useVersionChecker] Version mismatch: client=${currentVersion.current}, server=${serverVersion}. Reloading once.`);
                            sessionStorage.setItem(VERSION_RELOAD_KEY, '1');
                            window.location.reload();
                        }
                    }
                }
            } catch (error) {
                console.warn('[useVersionChecker] Failed to check version:', error);
            }
        };

        void checkVersion();
    }, []);
};

export default useVersionChecker;




